// Shared, presentation-only helpers for the diagnostic report redesign.
// No metric formulas are defined or changed here — this is display logic only.

// Traffic-light colour for a 0–100 score.
export const scoreColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 80) return 'var(--z-success)'; // strong
  if (s >= 50) return 'var(--z-warning)'; // moderate
  return 'var(--z-danger)';              // needs work
};

// Soft background tint that pairs with scoreColor.
export const scoreTint = (score) => {
  const s = Number(score) || 0;
  if (s >= 80) return 'var(--z-success-tint)';
  if (s >= 50) return 'var(--z-warning-tint)';
  return 'var(--z-danger-tint)';
};

// A translucent version of ANY css colour (hex, rgb or a var(--token)) — use instead of appending hex-alpha digits,
// which cannot work on a CSS variable.
export const withAlpha = (color, pct = 12) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

// Short qualitative rating for a score.
export const scoreBand = (score) => {
  const s = Number(score) || 0;
  if (s >= 85) return 'Excellent';
  if (s >= 70) return 'Good';
  if (s >= 50) return 'Fair';
  return 'Needs Work';
};

// Format seconds as "1m 5s" / "45s".
export const formatTime = (seconds) => {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return '—';
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
};

// Readiness-level → color/label. Mirrors the map already used inline in
// StudentPortal.js (kept in sync manually rather than imported from a page
// component) so the admin drill-down pages show the same colors/labels a
// student sees for the identical `readiness.level` value.
const READINESS_LEVEL_COLOR = {
  top_performer: 'var(--z-success)',
  placement_ready: 'var(--z-cyan)',
  moderate: 'var(--z-warning)',
  needs_improvement: 'var(--z-danger)',
};
const READINESS_LEVEL_LABEL = {
  top_performer: 'Top Performer',
  placement_ready: 'Placement Ready',
  moderate: 'Moderate',
  needs_improvement: 'Needs Improvement',
};
export const readinessLevelColor = (level) => READINESS_LEVEL_COLOR[level] || 'var(--z-muted)';
export const readinessLevelLabel = (level) => READINESS_LEVEL_LABEL[level] || 'Not evaluated';

// Plain-language meaning for each behavioural metric. Kept free of formulas.
export const BEHAVIOUR_META = {
  TPSI: {
    label: 'Time Pressure Stability',
    meaning: 'How well accuracy holds up as time pressure builds toward the end of the assessment.',
    strong: 'Stays composed and accurate even when time runs short.',
    improve: 'Practise timed sets so accuracy holds through the final questions.'
  },
  CER: {
    label: 'Cognitive Efficiency',
    meaning: 'The balance between speed and correctness while solving questions.',
    strong: 'Solves efficiently — good pace without sacrificing accuracy.',
    improve: 'Refine solving methods to answer faster with fewer second-guesses.'
  },
  RCI: {
    label: 'Risk Strategy',
    meaning: 'How wisely difficult questions are attempted versus skipped.',
    strong: 'Makes smart choices about which hard questions to attempt.',
    improve: 'Learn to spot and move past low-value hard questions sooner.'
  },
  FFI: {
    label: 'Focus & Fatigue',
    meaning: 'Consistency of focus and stamina across the full assessment.',
    strong: 'Maintains strong focus with little sign of fatigue.',
    improve: 'Build stamina with longer practice sessions and fewer distractions.'
  }
};
